import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useToast } from "@/hooks/use-toast";
import { Button } from '@/components/ui/button';
import { Toaster } from "@/components/ui/toaster";
import { Loader2, ArrowLeft, Download, Save, Edit3, FileText } from 'lucide-react';
import { apiService } from '@/config/api';
import VTechLogo from '@/components/VTechLogo';

// Import OpenResume components
import { Provider } from "react-redux";
import { store } from "../lib/redux/store";
import { Resume } from "../components/Resume";
import { setResume, selectResume } from "../lib/redux/resumeSlice";
import { useAppSelector } from "../lib/redux/hooks";
import { useDispatch } from 'react-redux';
import { changeSettings } from '../lib/redux/settingsSlice';

interface ResumeData {
  id: string;
  fileName: string;
  resumeStoragePath: string;
  parsedDetails: any;
  openResumeData?: any;
  sessionToken?: string;
}

// Custom hook to prevent localStorage override when loading from API
const useResumeEditorStore = () => {
  const dispatch = useDispatch();
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    // Only load from localStorage if we haven't loaded data from API/sessionStorage
    if (!isDataLoaded) {
      const savedResume = localStorage.getItem("resume");
      const savedSettings = localStorage.getItem("settings");

      if (savedResume) {
        try {
          const parsedResume = JSON.parse(savedResume);
          dispatch(setResume(parsedResume));
        } catch (error) {
          console.error("Failed to parse saved resume:", error);
        }
      }

      if (savedSettings) {
        try {
          const parsedSettings = JSON.parse(savedSettings);
          dispatch(changeSettings({ field: "theme", value: parsedSettings.theme }));
          dispatch(changeSettings({ field: "formsOrder", value: parsedSettings.formsOrder }));
        } catch (error) {
          console.error("Failed to parse saved settings:", error);
        }
      }
    }
  }, [dispatch, isDataLoaded]);

  return { setIsDataLoaded };
};

// Custom ResumeForm component that doesn't load from localStorage
const ResumeEditorForm = () => {
  const formsOrder = useAppSelector(selectFormsOrder);
  const [isHover, setIsHover] = useState(false);

  return (
    <div
      className={`h-full overflow-y-auto scrollbar-thin scrollbar-track-gray-300 bg-white ${
        isHover ? "scrollbar-thumb-gray-600" : "scrollbar-thumb-gray-400"
      }`}
      onMouseOver={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <section className="flex flex-col gap-8 p-6 max-w-2xl mx-auto pb-20">
        <ThemeForm />
        <ProfileForm />
        {formsOrder.map((form) => {
          const Component = formTypeToComponent[form];
          return <Component key={form} />;
        })}
        <br />
      </section>
    </div>
  );
};

// Import the form components we need
import { ProfileForm } from "../components/ResumeForm/ProfileForm";
import { WorkExperiencesForm } from "../components/ResumeForm/WorkExperiencesForm";
import { EducationsForm } from "../components/ResumeForm/EducationsForm";
import { ProjectsForm } from "../components/ResumeForm/ProjectsForm";
import { SkillsForm } from "../components/ResumeForm/SkillsForm";
import { ThemeForm } from "../components/ResumeForm/ThemeForm";
import { CustomForm } from "../components/ResumeForm/CustomForm";
import { selectFormsOrder } from "../lib/redux/settingsSlice";

const formTypeToComponent: { [type: string]: () => JSX.Element } = {
  workExperiences: WorkExperiencesForm,
  educations: EducationsForm,
  projects: ProjectsForm,
  skills: SkillsForm,
  custom: CustomForm,
};

// Inner component that uses Redux hooks
const ResumeEditorContent = () => {
  const { resumeId } = useParams<{ resumeId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currentResumeData = useAppSelector(selectResume);
  const { setIsDataLoaded } = useResumeEditorStore();
  
  // Ensure resumeId is defined
  if (!resumeId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <h2 className="text-xl font-semibold text-foreground">Invalid Resume ID</h2>
          <p className="text-muted-foreground">Please upload a resume first.</p>
          <Button onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }
  
  const [resumeData, setResumeData] = useState<ResumeData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user has uploaded a resume
    const uploadedResumeData = sessionStorage.getItem('vtechUploadedResumeData');
    
    if (!uploadedResumeData && resumeId === 'new') {
      // No uploaded resume data and trying to create new resume
      setError('Please upload a resume first to use the Resume Editor.');
      setIsLoading(false);
      return;
    }
    
    if (resumeId) {
      loadResumeData();
    } else {
      // If no resumeId, we're creating a new resume
      setIsLoading(false);
      setResumeData({
        id: 'new',
        fileName: 'New Resume',
        resumeStoragePath: '',
        parsedDetails: null
      });
    }
  }, [resumeId]);

  const loadResumeData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Check if user has uploaded a resume (for existing resume access)
      const uploadedResumeData = sessionStorage.getItem('vtechUploadedResumeData');
      
      if (!uploadedResumeData) {
        setError('Please upload a resume first to use the Resume Editor.');
        setIsLoading(false);
        return;
      }

      // Parse the uploaded resume data from sessionStorage
      const parsedUploadedData = JSON.parse(uploadedResumeData);

      try {
        // Try to fetch from API first
        const response = await apiService.get(`/api/resume-editor/${resumeId}`);
        
        if (response.success) {
          setResumeData(response.data);
          
          // Load the resume data into Redux store
          if (response.data.openResumeData) {
            store.dispatch(setResume(response.data.openResumeData));
            setIsDataLoaded(true); // Mark that we've loaded data
          }
        } else {
          throw new Error(response.error || 'Failed to load resume data from API');
        }
      } catch (apiError: any) {
        // Fallback to sessionStorage data
        if (parsedUploadedData && parsedUploadedData.resumeId === resumeId) {
          // Transform the parsed details to OpenResume format
          const openResumeFormat = transformToOpenResumeFormat(parsedUploadedData.parsedDetails);
          
          setResumeData({
            id: resumeId,
            fileName: parsedUploadedData.fileName,
            resumeStoragePath: parsedUploadedData.resumeStoragePath || '',
            parsedDetails: parsedUploadedData.parsedDetails,
            openResumeData: openResumeFormat
          });
          
          // Load the resume data into Redux store
          store.dispatch(setResume(openResumeFormat));
          setIsDataLoaded(true); // Mark that we've loaded data
        } else {
          throw new Error('Resume data not found in session storage');
        }
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load resume data');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to transform parsed resume data to OpenResume format
  const transformToOpenResumeFormat = (parsedData: any) => {
    const { name, email, phone, location, summary, experience, education, skills } = parsedData;

    return {
      profile: {
        name: name || '',
        email: email || '',
        phone: phone || '',
        location: location || '',
        url: '',
        summary: summary || ''
      },
      workExperiences: experience ? experience.map((exp: any) => ({
        company: exp.company || '',
        jobTitle: exp.title || '',
        date: exp.duration || '',
        descriptions: exp.descriptions || []
      })) : [{
        company: '',
        jobTitle: '',
        date: '',
        descriptions: []
      }],
      educations: education ? education.map((edu: any) => ({
        school: edu.school || '',
        degree: edu.degree || '',
        gpa: edu.gpa || '',
        date: edu.year || '',
        descriptions: edu.descriptions || []
      })) : [{
        school: '',
        degree: '',
        gpa: '',
        date: '',
        descriptions: []
      }],
      projects: [{
        project: '',
        date: '',
        descriptions: []
      }],
      skills: {
        featuredSkills: skills ? skills.slice(0, 6).map((skill: string) => ({
          skill: skill,
          rating: 4
        })) : Array(6).fill({ skill: '', rating: 4 }),
        descriptions: []
      },
      custom: {
        descriptions: []
      }
    };
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      // Get current resume state from Redux store
      const currentState = store.getState();
      const resumeState = currentState.resume;
      
      if (resumeId === 'new') {
        // For new resumes, we'll save to localStorage for now
        localStorage.setItem('newResumeData', JSON.stringify(resumeState));
        toast({
          title: "Success",
          description: "Resume saved to local storage",
        });
      } else {
        const response = await apiService.post(`/api/resume-editor/${resumeId}/save`, {
          resumeData: resumeState
        });
        
        if (response.success) {
          toast({
            title: "Success",
            description: "Resume saved successfully",
          });
        } else {
          throw new Error(response.error || 'Failed to save resume');
        }
      }
    } catch (error: any) {
      console.error('Error saving resume:', error);
      toast({
        title: "Error",
        description: error.message || 'Failed to save resume',
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      
      if (resumeId === 'new') {
        // For new resumes, generate PDF on the backend with current data
        const response = await apiService.post(`/api/resume-editor/new/download`, {
          resumeData: currentResumeData
        }, {
          responseType: 'blob'
        });
        
        // Create download link
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'new-resume.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast({
          title: "Success",
          description: "Resume downloaded successfully",
        });
      } else {
        const response = await apiService.post(`/api/resume-editor/${resumeId}/download`, {
          resumeData: currentResumeData
        }, {
          responseType: 'blob'
        });
        
        // Create download link
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${resumeData?.fileName || 'resume'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast({
          title: "Success",
          description: "Resume downloaded successfully",
        });
      }
    } catch (error: any) {
      console.error('Error downloading resume:', error);
      toast({
        title: "Error",
        description: error.message || 'Failed to download resume',
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center space-y-4"
        >
          <div className="p-4 bg-primary/10 rounded-full w-20 h-20 mx-auto flex items-center justify-center">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-foreground">Loading Resume Editor</h2>
            <p className="text-muted-foreground">Preparing your resume for editing...</p>
          </div>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center space-y-4 max-w-md mx-auto p-6"
        >
          <div className="p-4 bg-red-500/10 rounded-full w-20 h-20 mx-auto flex items-center justify-center">
            <FileText className="h-10 w-10 text-red-500" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-foreground">Error Loading Resume</h2>
            <p className="text-muted-foreground">{error}</p>
          </div>
          <Button onClick={handleBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster />
      
      {/* Header */}
      <motion.header 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-40"
      >
        <div className="container-professional px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <VTechLogo />
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center space-x-2">
              <Edit3 className="h-5 w-5 text-primary" />
              <span className="font-semibold">Resume Editor</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center space-x-2"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>Save</span>
            </Button>
            
            <Button
              onClick={handleDownload}
              disabled={isDownloading}
              className="flex items-center space-x-2"
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span>Download PDF</span>
            </Button>
            
            <Button
              variant="ghost"
              onClick={handleBack}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </Button>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="relative h-[calc(100vh-var(--top-nav-bar-height))] w-full bg-gray-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
          <div className="h-full border-r-2 border-black">
            <ResumeEditorForm />
          </div>
          <div className="h-full">
            <Resume />
          </div>
        </div>
      </main>
    </div>
  );
};

// Main component that wraps everything in Redux Provider
export default function ResumeEditorPage() {
  return (
    <Provider store={store}>
      <ResumeEditorContent />
    </Provider>
  );
}
